/**
 * React Query hooks for per-section access control.
 * Provides data fetching, caching, and mutations for the Binom/Keitaro-style access system.
 */

import {
    accessApi,
    type AccessLevel,
    type Section,
    type SetUserAccessRequest,
} from '@/api/access'
import { useTeamStore } from '@/store/team'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'


// =============================================================================
// Query Keys
// =============================================================================

export const accessKeys = {
  all: ['access'] as const,
  effective: () => [...accessKeys.all, 'effective'] as const,
  user: (teamId: string, userId: string) =>
    [...accessKeys.all, 'user', teamId, userId] as const,
  entities: (teamId: string, section: string) =>
    [...accessKeys.all, 'entities', teamId, section] as const,
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetch the current user's effective access for the active team.
 * Automatically loads on mount and stores result in team store.
 */
export function useEffectiveAccess() {
  const currentTeamId = useTeamStore((s) => s.currentTeamId)
  const setAccess = useTeamStore((s) => s.setAccess)

  return useQuery({
    queryKey: accessKeys.effective(),
    queryFn: async () => {
      const data = await accessApi.getEffectiveAccess()
      setAccess(data)
      return data
    },
    enabled: !!currentTeamId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch another user's access config (for Settings UI / Admin view).
 */
export function useUserAccess(teamId: string, userId: string) {
  return useQuery({
    queryKey: accessKeys.user(teamId, userId),
    queryFn: () => accessApi.getUserAccess(teamId, userId),
    enabled: !!teamId && !!userId,
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Fetch entities for the entity picker in "Selected" mode.
 * Supports debounced search.
 */
export function useSectionEntities(
  teamId: string,
  section: Section,
  search?: string,
  enabled = true
) {
  return useQuery({
    queryKey: [...accessKeys.entities(teamId, section), search],
    queryFn: () =>
      accessApi.getSectionEntities(teamId, section, {
        search: search || undefined,
        limit: 50,
        offset: 0,
      }),
    enabled: enabled && !!teamId,
    staleTime: 30 * 1000,
  })
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Configure a user's access for all sections (batch PUT).
 */
export function useSetUserAccess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      teamId,
      userId,
      data,
    }: {
      teamId: string
      userId: string
      data: SetUserAccessRequest
    }) => accessApi.setUserAccess(teamId, userId, data),
    onSuccess: (_data, variables) => {
      // Invalidate the specific user's access and the effective access
      queryClient.invalidateQueries({
        queryKey: accessKeys.user(variables.teamId, variables.userId),
      })
      queryClient.invalidateQueries({
        queryKey: accessKeys.effective(),
      })
    },
  })
}

/**
 * Reset a user's access to role defaults.
 */
export function useResetUserAccess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      accessApi.resetUserAccess(teamId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: accessKeys.user(variables.teamId, variables.userId),
      })
      queryClient.invalidateQueries({
        queryKey: accessKeys.effective(),
      })
    },
  })
}

// =============================================================================
// Convenience Hooks (read from store)
// =============================================================================

/**
 * Check if the current user can access a section.
 */
export function useCanAccess(section: Section): boolean {
  return useTeamStore((state) => state.canAccessSection(section))
}

/**
 * Check if the current user can create entities in a section.
 */
export function useCanCreate(section: Section): boolean {
  return useTeamStore((state) => state.canCreateIn(section))
}

/**
 * Get the current user's access level for a section.
 */
export function useAccessLevel(section: Section): AccessLevel {
  return useTeamStore((state) => state.getAccessLevel(section))
}

/**
 * Check if the current user's access for a section is read-only.
 */
export function useIsReadOnly(section: Section): boolean {
  return useTeamStore((state) => state.getAccessLevel(section) === 'read_only')
}

/**
 * Get all hidden report columns for the current user.
 */
export function useHiddenColumns(): string[] {
  return useTeamStore((state) => state.getHiddenColumns())
}
